class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') {
        this.close();
      }
    });

    // The overlay is replaced when the drawer contents are rendered, so keep
    // this listener on the persistent custom element.
    this.addEventListener('click', this.handleDrawerClick.bind(this));

    this.setHeaderCartIconAccessibility();
  }

  handleDrawerClick(event) {
    if (event.target.closest('#CartDrawer-Overlay')) {
      event.preventDefault();
      this.close();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains(
          'is-empty'
        )
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (
      cartDrawerNote.nextElementSibling.getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      const details = event.currentTarget.closest('details');

      event.currentTarget.setAttribute(
        'aria-expanded',
        !details.hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  isFreeGift(item = {}) {
    const freeGiftTitle = 'epii double up cream';

    const itemTitle =
      item.product_title ||
      item.title ||
      '';

    return (
      itemTitle.trim().toLowerCase() === freeGiftTitle
    );
  }

  getNormalAddedItem(parsedState = {}, addedItem = {}) {
    /*
     * When Shopify adds multiple products, its response contains
     * an `items` array. Find the first product that is not the gift.
     */
    if (Array.isArray(parsedState.items)) {
      const normalItem = parsedState.items.find(
        (item) => !this.isFreeGift(item)
      );

      if (normalItem) {
        return {
          title:
            normalItem.product_title ||
            normalItem.title ||
            'Added to cart',

          image:
            normalItem.featured_image?.url ||
            normalItem.image ||
            '',

          variantId:
            normalItem.variant_id ||
            normalItem.id ||
            '',
        };
      }
    }

    /*
     * Shopify returns a single item object for regular additions.
     */
    if (
      parsedState &&
      !Array.isArray(parsedState.items) &&
      !this.isFreeGift(parsedState) &&
      (parsedState.product_title || parsedState.title)
    ) {
      return {
        title:
          parsedState.product_title ||
          parsedState.title ||
          'Added to cart',

        image:
          parsedState.featured_image?.url ||
          parsedState.image ||
          '',

        variantId:
          parsedState.variant_id ||
          parsedState.id ||
          '',
      };
    }

    /*
     * Fall back to the product information supplied by product-form.
     */
    if (!this.isFreeGift(addedItem)) {
      return addedItem;
    }

    /*
     * Only the free gift was found, so no toast should be displayed.
     */
    return null;
  }

  renderContents(
    parsedState,
    addedItem = {},
    showToast = true
  ) {
    const drawerInner = this.querySelector(
      '.drawer__inner'
    );

    if (
      drawerInner &&
      drawerInner.classList.contains('is-empty')
    ) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHTML =
        parsedState.sections?.[section.id];

      if (!sectionElement || !sectionHTML) {
        return;
      }

      sectionElement.innerHTML =
        this.getSectionInnerHTML(
          sectionHTML,
          section.selector
        );
    });

    document.dispatchEvent(
      new CustomEvent('cartUpdated', {
        detail: parsedState,
      })
    );

    if (showToast) {
      const toastItem = this.getNormalAddedItem(
        parsedState,
        addedItem
      );

      if (toastItem) {
        this.showAddedToCartToast(toastItem);
      }
    }
  }

  showAddedToCartToast(addedItem = {}) {
    /*
     * Final safety check: never display a toast for the gift.
     */
    if (this.isFreeGift(addedItem)) {
      return;
    }

    let toast = document.getElementById(
      'cart-added-toast'
    );

    const viewCartUrl =
      window.routes?.cart_url || '/cart';

    const itemTitle =
      addedItem.title || 'Added to cart';

    const itemImage =
      addedItem.image || '';

    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'cart-added-toast';
      toast.className = 'cart-added-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');

      document.body.appendChild(toast);
    }

    toast.innerHTML = `
      <div class="cart-added-toast__content">
        <div class="cart-added-toast__check">
          <svg
            class="cart-added-toast__check-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M20 6L9 17l-5-5"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            ></path>
          </svg>
        </div>

        <div class="cart-added-toast__preview">
          ${
            itemImage
              ? `
                <img
                  src="${itemImage}"
                  alt="${itemTitle}"
                  class="cart-added-toast__image"
                >
              `
              : `
                <div
                  class="cart-added-toast__image cart-added-toast__image--fallback"
                ></div>
              `
          }
        </div>

        <div class="cart-added-toast__text">
          <div class="cart-added-toast__eyebrow">
            Added to cart
          </div>

          <div class="cart-added-toast__title">
            ${itemTitle}
          </div>
        </div>
      </div>

      <a
        href="${viewCartUrl}"
        class="cart-added-toast__button"
      >
        View cart
      </a>
    `;

    toast.classList.add('is-visible');

    clearTimeout(this.toastTimeout);

    this.toastTimeout = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 2800);
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    const documentHTML = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const section = documentHTML.querySelector(selector);

    return section ? section.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}
